import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { delay, filter, map } from 'rxjs/operators';
import { LoadingService } from './loading.service';
import { uniq } from 'lodash';
import { NotificationsComponent } from './components/controls/notifications/notifications.component';
import { ToastComponent } from './components/controls/toast/toast.component';
import { StaleBuildService, staleBuildToastKey } from './stale-build.service';

@Component({
	standalone: true,
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	imports: [RouterOutlet, NotificationsComponent, ToastComponent],
})
export class AppComponent implements OnInit, OnDestroy {
	private router = inject(Router);
	private activatedRoute = inject(ActivatedRoute);
	private titleService = inject(Title);
	private loadingService = inject(LoadingService);
	private staleBuildService = inject(StaleBuildService);

	readonly staleBuildToastKey = staleBuildToastKey;

	// Signals so the loading spinner reads are glitch-free: the loading/showSpinner state is driven
	// by async sources (HTTP interceptor via LoadingService, router events) that can settle around a
	// change-detection pass, which otherwise produces NG0100 ExpressionChangedAfterItHasBeenChecked.
	loading = signal(false);
	showSpinner = signal(false);
	loadingSubscription: Subscription;
	routerEventsSubscription: Subscription;

	constructor() {
		this.setupDocumentTitle();
	}

	ngOnInit(): void {
		this.listenToLoading();
		this.staleBuildService.monitor();
	}

	ngOnDestroy(): void {
		this.loadingSubscription?.unsubscribe();
		this.routerEventsSubscription?.unsubscribe();
	}

	/** Called from the "new version available" toast action. */
	reloadToLatestVersion(): void {
		this.staleBuildService.reloadToLatestVersion();
	}

	/**
	 * Listen to the loadingSub property in the LoadingService class. This drives the
	 * display of the loading spinner.
	 */
	listenToLoading(): void {
		this.loadingSubscription = this.loadingService.loadingSub
			// This prevents a ExpressionChangedAfterItHasBeenCheckedError for subsequent requests
			.pipe(delay(0))
			.subscribe((loadingOptions) => {
				if (loadingOptions.target === LoadingService.DEFAULT_TARGET) {
					this.loading.set(loadingOptions.loading);
				}
			});
	}

	private setupDocumentTitle(): void {
		// Recursively setup document title from route data['documentTitle']
		this.routerEventsSubscription = this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map(() => {
					const titles = [];
					let child = this.activatedRoute.firstChild;

					// set the showSpinner variable - hides the loading spinner on login page
					this.showSpinner.set(child.snapshot.children[0]?.data.documentTitle !== 'Login');

					while (child) {
						if (child.snapshot.data) {
							if (child.snapshot.data['letComponentSetDocumentTitle']) {
								return null;
							} else if (child.snapshot.data['documentTitle']) {
								titles.push(child.snapshot.data['documentTitle']);
							}
						}
						child = child.firstChild;
					}
					return titles;
				})
			)
			.subscribe((documentTitles: string[]) => {
				if (documentTitles != null) {
					const documentTitle = [...uniq(documentTitles), 'IDS VM Online'].filter((x) => x).join(' - ');
					this.titleService.setTitle(documentTitle);
				}
			});
	}
}
